import { useCallback, useState } from "react";
import { AxiosError } from "axios";
import { downloadETaxSignedPdf } from "../../services";
import { pushGlobalError } from "../../errors/globalError";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../../components/SalesReportExport/utils";

// Long enough for a slow tab to finish loading the blob before it is released.
const SIGNED_PDF_URL_TTL_MS = 60_000;

export const ETAX_SIGNED_PDF_ERROR_MESSAGE =
  "ไม่สามารถเปิดใบกำกับภาษีอิเล็กทรอนิกส์ได้ กรุณาลองใหม่อีกครั้ง";

// A 404 here is not a broken link: KTB simply has nothing signed for this document yet
// (still in flight, or the signing failed). Saying "try again" would be misleading.
export const ETAX_SIGNED_PDF_NOT_FOUND_MESSAGE =
  "ยังไม่มีใบกำกับภาษีที่ลงลายมือชื่อจากผู้ให้บริการ";

/**
 * Shared handler for the KTB-signed e-tax document (ใบกำกับภาษีที่ลงลายมือชื่อ).
 *
 * The signed file lives at KTB, not in our storage — the API proxies
 * `DownloadSignedFileService` and streams the bytes back same-origin, so this hook always
 * goes through the API rather than any object-storage URL.
 *
 * `view` opens the document in a new tab. The tab is opened synchronously on click and
 * only then pointed at the blob, because a popup opened after an `await` is blocked by
 * default in every major browser.
 */
export function useEtaxSignedPdf() {
  const [busyId, setBusyId] = useState<string | null>(null);

  const reportFailure = useCallback((error: unknown) => {
    const status = (error as AxiosError).response?.status;
    pushGlobalError(
      status === 404
        ? ETAX_SIGNED_PDF_NOT_FOUND_MESSAGE
        : ETAX_SIGNED_PDF_ERROR_MESSAGE,
    );
  }, []);

  const fetchSignedPdf = useCallback(async (etaxUuid: string) => {
    const response = await downloadETaxSignedPdf(etaxUuid);
    const blob = response.data as Blob;

    // Guard against an invalid file response (empty body, or a JSON error payload
    // returned with a success status).
    if (
      !(blob instanceof Blob) ||
      blob.size === 0 ||
      blob.type.includes("application/json")
    ) {
      throw new Error("Invalid signed PDF response");
    }

    return { blob, headers: response.headers };
  }, []);

  const viewSignedPdf = useCallback(
    async (etaxUuid: string | null | undefined) => {
      if (!etaxUuid || busyId) return;

      // Opened before the request so the browser attributes it to the click.
      const tab = window.open("", "_blank");
      setBusyId(etaxUuid);
      try {
        const { blob } = await fetchSignedPdf(etaxUuid);
        const url = URL.createObjectURL(
          blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" }),
        );

        if (tab) {
          tab.location.href = url;
          // The tab keeps its own copy once it has loaded the PDF; holding the blob URL for
          // the page's lifetime would leak one document per click.
          window.setTimeout(() => URL.revokeObjectURL(url), SIGNED_PDF_URL_TTL_MS);
        } else {
          // Popup blocked despite the synchronous open — fall back to saving the file
          // so the click still does something useful.
          downloadBlob("signed-tax-invoice.pdf", blob);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        tab?.close();
        reportFailure(error);
      } finally {
        setBusyId(null);
      }
    },
    [busyId, fetchSignedPdf, reportFailure],
  );

  const downloadSignedPdf = useCallback(
    async (etaxUuid: string | null | undefined, filename: string) => {
      if (!etaxUuid || busyId) return;

      setBusyId(etaxUuid);
      try {
        const { blob, headers } = await fetchSignedPdf(etaxUuid);
        const resolvedFilename =
          getFilenameFromContentDisposition(
            headers["content-disposition"] as string | undefined,
          ) || filename;

        downloadBlob(resolvedFilename, blob);
      } catch (error) {
        reportFailure(error);
      } finally {
        setBusyId(null);
      }
    },
    [busyId, fetchSignedPdf, reportFailure],
  );

  return { signedPdfBusyId: busyId, viewSignedPdf, downloadSignedPdf };
}
